import {compareMentionsByPublishedDate} from "@/conversation/utils/MentionConversationUtils";

/** A single reply tree. All walks are iterative, including 50,000-deep ancestor chains. */
export class ConversationTree {
    constructor(mentions, headId) {
        this.headId = headId;
        this.nodes = new Map();
        for (const mention of mentions) {
            if (!mention?.id || mention.reshareOfId) continue;
            this.nodes.set(mention.id, {...mention, parentId: mention.replyToId ?? null, children: [], unavailable: mention.unavailable ?? false});
        }
        const unavailable = (id, parentId = null) => ({id, parentId, children: [], unavailable: true});
        if (!this.nodes.has(headId)) this.nodes.set(headId, unavailable(headId));
        for (const node of this.nodes.values()) {
            if (node.parentId && !this.nodes.has(node.parentId)) {
                this.nodes.set(node.parentId, unavailable(node.parentId));
            }
        }
        this.nodes.get(headId).parentId = null;
        const connectionId = `unavailable:${headId}`;
        const connect = node => {
            if (!this.nodes.has(connectionId)) this.nodes.set(connectionId, unavailable(connectionId, headId));
            node.parentId = connectionId;
        };
        // Missing connections and cycles are explicit gaps, never independent roots or invented replies.
        const complete = new Set([headId]);
        for (const start of this.nodes.values()) {
            if (complete.has(start.id)) continue;
            const path = new Set();
            let node = start;
            while (node && !complete.has(node.id)) {
                if (path.has(node.id)) {
                    connect(node);
                    break;
                }
                path.add(node.id);
                if (!node.parentId) {
                    connect(node);
                    break;
                }
                node = this.nodes.get(node.parentId);
            }
            for (const id of path) complete.add(id);
        }
        for (const node of this.nodes.values()) {
            if (node.parentId) this.nodes.get(node.parentId).children.push(node.id);
        }
        for (const node of this.nodes.values()) {
            node.children.sort((a, b) => compareMentionsByPublishedDate(this.nodes.get(a), this.nodes.get(b)) || a.localeCompare(b));
        }
        this.order = [];
        const stack = [headId];
        while (stack.length) {
            const id = stack.pop();
            this.order.push(id);
            const children = this.nodes.get(id).children;
            for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
        }
        this.sizes = new Map();
        for (let i = this.order.length - 1; i >= 0; i--) {
            const node = this.nodes.get(this.order[i]);
            this.sizes.set(node.id, (node.unavailable ? 0 : 1) + node.children.reduce((n, id) => n + this.sizes.get(id), 0));
        }
    }

    getMention(id) { return this.nodes.get(id) ?? null; }

    /** Select a bounded neighbourhood, rather than loading every ancestor of the selection. */
    initialIds(selectedId, limit = 20) {
        const ids = new Set();
        const add = id => {
            if (ids.size < limit && this.nodes.has(id) && !this.nodes.get(id).unavailable) ids.add(id);
        };
        add(this.headId);
        add(selectedId);
        let parent = this.nodes.get(selectedId)?.parentId;
        for (let i = 0; i < 2 && parent; i++) {
            add(parent);
            parent = this.nodes.get(parent)?.parentId;
        }
        const selected = this.nodes.get(selectedId);
        const siblings = this.nodes.get(selected?.parentId)?.children ?? [];
        const index = siblings.indexOf(selectedId);
        for (const id of siblings.slice(Math.max(0, index - 2), index + 3)) add(id);
        const queue = [...(selected?.children ?? []), ...this.nodes.get(this.headId).children];
        for (let i = 0; i < queue.length && ids.size < limit; i++) {
            const node = this.nodes.get(queue[i]);
            add(node.id);
            for (const id of node.children) queue.push(id);
        }
        return [...ids];
    }

    /** Rows describe the rendered tree: parentId refers to a rendered row, not a skipped ancestor. */
    project(exposed, mentionsById) {
        const required = new Set([this.headId]);
        for (let i = this.order.length - 1; i >= 0; i--) {
            const node = this.nodes.get(this.order[i]);
            if (exposed.has(node.id) || required.has(node.id)) {
                required.add(node.id);
                if (node.parentId) required.add(node.parentId);
            }
        }
        const rows = [];
        const expansions = new Map();
        const stack = [{id: this.headId, parentId: null, depth: 0}];
        const row = (id, parentId, depth, kind, count, mention = null) => ({
            id, parentId, depth, mention, kind, count, reshares: [], reshareTotal: null,
            resharesLoaded: false, loadingReshares: false
        });
        while (stack.length) {
            const task = stack.pop();
            if (task.more) {
                rows.push(row(task.id, task.parentId, task.depth, 'more', task.count));
                expansions.set(task.id, task.hidden);
                continue;
            }
            let node = this.nodes.get(task.id);
            const skipped = [];
            const before = [];
            const after = [];
            // Preserve the tree order around a compressed ancestor path. Side replies
            // before the path precede its visible child; later replies follow it.
            while (!exposed.has(node.id) && node.id !== this.headId && !node.unavailable) {
                const nextIds = node.children.filter(id => required.has(id));
                if (nextIds.length !== 1) break;
                skipped.push(node.id);
                const index = node.children.indexOf(nextIds[0]);
                before.push(...node.children.slice(0, index));
                after.push(node.children.slice(index + 1));
                node = this.nodes.get(nextIds[0]);
            }
            let current;
            if (skipped.length) {
                current = row(`gap:${task.id}`, task.parentId, task.depth, 'gap', skipped.length);
                expansions.set(current.id, skipped);
                // The last skipped node is represented by the gap; continue with its child.
                node = this.nodes.get(skipped[skipped.length - 1]);
            } else {
                const mention = mentionsById.get(node.id) ?? null;
                const kind = mention ? 'mention' : node.unavailable || exposed.has(node.id) ? 'unavailable' : 'gap';
                current = row(node.id, task.parentId, task.depth, kind, 1, mention);
                if (!node.unavailable && !exposed.has(node.id)) expansions.set(current.id, [node.id]);
            }
            rows.push(current);
            const children = skipped.length
                ? [...before, ...node.children.filter(id => required.has(id)), ...after.reverse().flat()]
                : node.children;
            const tasks = [];
            let hidden = [];
            const flushHidden = () => {
                if (!hidden.length) return;
                tasks.push({id: `more:${current.id}:${hidden[0]}`, parentId: current.id,
                    depth: task.depth + 1, more: true,
                    count: hidden.reduce((n, id) => n + this.sizes.get(id), 0), hidden});
                hidden = [];
            };
            for (const id of children) {
                if (required.has(id)) {
                    flushHidden();
                    tasks.push({id, parentId: current.id, depth: task.depth + 1});
                } else {
                    hidden.push(id);
                }
            }
            flushHidden();
            for (let i = tasks.length - 1; i >= 0; i--) stack.push(tasks[i]);
        }
        return {rows, expansions};
    }

    expansionIds(startIds, exposed, limit = 20, includeDescendants = true) {
        const result = [];
        const queue = [...startIds];
        const seen = new Set();
        for (let i = 0; i < queue.length && result.length < limit; i++) {
            const node = this.nodes.get(queue[i]);
            if (!node || seen.has(node.id)) continue;
            seen.add(node.id);
            if (!node.unavailable && !exposed.has(node.id)) result.push(node.id);
            // Intervening mentions are an explicit chain, not permission to expand side replies.
            if (includeDescendants) for (const id of node.children) queue.push(id);
        }
        return result;
    }
}

export const createConversationTree = (mentions, headId) => new ConversationTree(mentions, headId);
